import React, { Component } from 'react';
import { strings } from '../localization';
import ConfirmDialog from "./common/ConfirmDialog";
import InfoTooltip from "./InfoTooltip";

class TreeNode extends Component {
    
    toggleChecked = () => {
        if(this.props.readonly) return;
        this.props.onToggleSelection(this.props.value);
    }

    toggleExpanded = () => {
        this.props.onToggleExpansion(this.props.value);
    }

    onMouseEnter = () => {
        const correspondingElements = document.querySelectorAll(`.n${this.props.value}`);
        correspondingElements.forEach(element => {
            element.classList.add('highlight');
        });
    }

    onMouseLeave = (e) => {
        if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
            const correspondingElements = document.querySelectorAll(`.n${this.props.value}`);
            correspondingElements.forEach(element => {
                element.classList.remove('highlight');
            });
        }
    }

    render() {
        const {isSelected, isExpanded, children, hasChildNodes, label, value, matchedFilters, showTooltips, tooltip} = this.props;

        let className = isSelected ? 'checked' : 'unchecked';
        if(this.props.readonly) className += ' readonly';

        const expansionIcon =  isExpanded ? 'remove' : 'add';
        const selectionIcon = isSelected ? 'check_box' : 'check_box_outline_blank';

        return (
            <li className={hasChildNodes ? isExpanded ? 'branch expanded' : 'branch' : 'leaf'}>
                <div onMouseEnter={this.onMouseEnter} onMouseOut={this.onMouseLeave}>
                    {hasChildNodes ? <i onClick={this.toggleExpanded} className={`material-icons expansion-icon`}>{expansionIcon}</i> : null}
                    <i onClick={this.toggleChecked} className={`material-icons selection-icon ${className}`} data-nodeid={showTooltips ? value : ''}>{selectionIcon}</i>
                    <span className={matchedFilters? 'matched-filters' : ''}>{label}</span>
                    {(showTooltips && tooltip) && <InfoTooltip message={tooltip} for={`[data-nodeid="${value}"]`} position="bottom left" noIcon />}
                </div>
                {children && <div>{children}</div>}
            </li>
        );
    }
}

class TreeView extends Component {

    constructor(props){
        super(props);

        this.state = {
            nodes: this.setNodes(this.props.nodes),

            expandedKeys: this.setExpandedKeys(this.props.expandedKeys, this.props.selectedKeys),

            keyword: this.props.keywordFilterValue ? this.props.keywordFilterValue : '',
            customFilterValue: this.props.customFilterValue ? this.props.customFilterValue : undefined,

            isConfirmOpen: false,
            confirmMessage: '',
        }
    }

    componentDidMount(){
        
        if(this.state.keyword !== '' || this.state.customFilterValue){
            this.filterNodes();
        }
    }

    componentWillUnmount(){
        if(this.props.onKeywordFilterChange) this.props.onKeywordFilterChange(this.state.keyword);
        if(this.props.onExpandedKeysChange) this.props.onExpandedKeysChange(this.state.expandedKeys);
    }

    setExpandedKeys = (...args) => {

        // By default expand the root nodes
        // root nodes only have one ancestor which is the root node itself
        const rootNodes = this.props.nodes.filter(node => node[this.props.fields.ancestors].length === 1);

        const keys = rootNodes.map(node => node[this.props.fields.key]);

        args.forEach(arg => {
            if(arg){
                keys.push(...arg);
            }
        });

        const ancestorKeys = new Set();

        keys.forEach(key => {
            const node = this.props.nodes.find(node => node[this.props.fields.key] === key);
            if(node){
                if(node[this.props.fields.ancestors]){
                    node[this.props.fields.ancestors].forEach(ancestor => {;
                        ancestorKeys.add(ancestor);
                    });
                }
            }
        });
        
        return [...keys, ...ancestorKeys];
    }

    setNodes = (nodes) => {
        const nodesCopy = nodes ? JSON.parse(JSON.stringify(nodes)) : [];

        return this.sortNodes(nodesCopy);
    }

    sortNodes = (nodes) => {
        // sort nodes by label ascending
        return nodes.sort((a, b) => a[this.props.fields.label].localeCompare(b[this.props.fields.label]));
    }

    renderTree(nodes) {
        const { fields, readonly } = this.props;
        const className = readonly ? 'treeview readonly' : 'treeview';
        
        return (
            <ul className={className}>
                {nodes.map((node, index) => {

                    const isSelected = this.props.selectedKeys.some(key => key === node[fields.key]);
                    const isExpanded = this.state.expandedKeys.some(key => key === node[fields.key]);
                    const hasChildNodes = node[fields.childNodes] && node[fields.childNodes].length > 0;
                    const isReadOnly = this.props.readonly || (this.props.readonlyKeys && this.props.readonlyKeys.some(key => key === node[fields.key]));
                    const tooltip = this.props.tooltips ? this.props.tooltips[node[fields.key]] : undefined;
                    return <TreeNode 
                                value={node[fields.key]}
                                label={node[fields.label]}
                                isSelected={isSelected}
                                isExpanded={isExpanded}
                                hasChildNodes={hasChildNodes}
                                onToggleSelection={this.onNodeToggleSelection}
                                onToggleExpansion={this.onNodeToggleExpansion} 
                                key={node[fields.key]}
                                readonly={isReadOnly}
                                matchedFilters={node.matchedFilters}
                                tooltip={tooltip}
                                showTooltips={this.props.showTooltips}
                            >
                                {hasChildNodes && isExpanded && this.renderTree(node[fields.childNodes])}
                            </TreeNode>
                })}
            </ul>
        );
    }

    onNodeToggleSelection = (key) => {
        let selectedKeys = [...this.props.selectedKeys];

        if(selectedKeys.find(k => k === key)){
            if(this.props.multipleSelection){
                // Show a confirmation dialog before deselecting the node
                if(this.props.showConfrimOnDeselect){
                    this.openConfirmDialog(key);
                    return;
                }
                
                // Proceed with deselecting the node
                selectedKeys = selectedKeys.filter(k => k !== key);

                if(this.props.cascadeSelection){
                    const descendants = this.state.nodes.filter(node => node[this.props.fields.ancestors].includes(key));
                    const descendantKeys = descendants.map(node => node[this.props.fields.key]);
                    selectedKeys = selectedKeys.filter(k => !descendantKeys.includes(k));
                }
            }
        } else {
            if(this.props.multipleSelection){

                if(this.props.cascadeSelection){
                    const descendants = this.state.nodes.filter(node => node[this.props.fields.ancestors].includes(key));
                    const notSelectedDescendants = descendants.filter(node => !selectedKeys.includes(node[this.props.fields.key]));
                    const notSelectedDescendantsKeys = notSelectedDescendants.map(node => node[this.props.fields.key]);
                    selectedKeys = Array.from(new Set([...selectedKeys, ...notSelectedDescendantsKeys]));
                }
                else{
                    selectedKeys.push(key);
                }
            }
            else{
                selectedKeys = [key];
            }
        }

        this.props.onChange(selectedKeys);
    }

    deselectNode = () => {
        const key = this.state.keyPendingForDeselect;
        let selectedKeys = [...this.props.selectedKeys];
        selectedKeys = selectedKeys.filter(k => k !== key);

        if(this.props.cascadeSelection){
            const descendants = this.state.nodes.filter(node => node[this.props.fields.ancestors].includes(key));
            const descendantKeys = descendants.map(node => node[this.props.fields.key]);
            selectedKeys = selectedKeys.filter(k => !descendantKeys.includes(k));
        }

        this.setState({isConfirmOpen: false, confirmMessage: '', keyPendingForDeselect: null});
        this.props.onChange(selectedKeys);
    }

    openConfirmDialog = (key) => {
        const confirmMessage = typeof this.props.confirmMessage === 'function' ? this.props.confirmMessage(key) : this.props.confirmMessage;
        this.setState({isConfirmOpen: true, confirmMessage, keyPendingForDeselect: key});
    }

    closeConfirmDialog = () => {
        this.setState({isConfirmOpen: false, confirmMessage: '', keyPendingForDeselect: null});
    }

    onNodeToggleExpansion = (key) => {
        let expandedKeys = [...this.state.expandedKeys];

        if(expandedKeys.find(k => k === key)){
            expandedKeys = expandedKeys.filter(k => k !== key);
        } else {
            expandedKeys.push(key);
        }

        this.setState({expandedKeys});
    }

    convertToHierarchy(nodes){
        if(!nodes) return [];

        const { fields: {key, parentKey, childNodes} } = this.props;

        const nodesMap = {};
        const result = [];
    
        // create a map for easy access to each node by its id
        nodes.forEach(node => {
            nodesMap[node[key]] = { ...node, isLeaf: true, isRoot: false };
        });
    
        // build the tree structure
        nodes.forEach(node => {
            if (node[parentKey]) {
    
                let parent = nodesMap[node[parentKey]];
    
                if (parent) {
                    if(!parent[childNodes]) {
                        parent[childNodes] = [];
                    }

                    parent[childNodes].push(nodesMap[node[key]]);
                    parent.isLeaf = false;
                }
                else {
                    result.push(nodesMap[node[key]]);
                }
            }
            else {
                nodesMap[node[key]].isRoot = true;
                result.push(nodesMap[node[key]]);
            }
        });    
        
        return result;
    }

    onKeywordChange = (event) => {
        this.setState({keyword: event.target.value}, this.filterNodes);
        if(this.props.onKeywordFilterChange) this.props.onKeywordFilterChange(event.target.value);
    }

    onCustomFilterChange = (customFilterValue) => {
        if(!customFilterValue || !customFilterValue.length || customFilterValue.length === 0){
            this.setState({customFilterValue: undefined}, this.filterNodes);
            return;
        }
        this.setState({customFilterValue}, this.filterNodes);
    }

    filterNodes = () => {
        
        const { fields, nodes } = this.props;

        let list = [];

        const includeAncestorsInResults = (node) => {
            
            let currentNode = node;
    
            while(currentNode[fields.parentKey]){
                
                let parentNode = list.find(n => n[fields.key] === currentNode[fields.parentKey]);
                if(parentNode){
                    currentNode = parentNode;
                }else{
                    parentNode = nodes.find(n => n[fields.key] === currentNode[fields.parentKey]);
                    if(parentNode){
                        parentNode = JSON.parse(JSON.stringify(parentNode));
                        const alreadyAdded = list.find(n => n[fields.key] === parentNode[fields.key]);
                        if(!alreadyAdded){
                            list.push(parentNode);
                        }
                        currentNode = parentNode;
                    }
                    else{
                        break;
                    }
                }    
            }
        }

        if(!this.props.nodes) {
            this.setState({nodes: []});
            return;
        }

        const allNodes = this.setNodes(this.props.nodes);

        if(!this.state.keyword && !this.state.customFilterValue) {
            this.setState({nodes: allNodes});
            return;
        }

        allNodes.forEach(node => {
            const alreadyAdded = list.find(n => n[fields.key] === node[fields.key]);

            const matchesKeyword = this.state.keyword ? node[fields.label].toLowerCase().includes(this.state.keyword.toLowerCase()) : true;
            const matchesCustomFilter = this.props.customFilterHandler ? this.props.customFilterHandler(node) : true;

            if(matchesKeyword && matchesCustomFilter){
                if(alreadyAdded){
                    alreadyAdded.matchedFilters = true;
                    return;
                } 

                node.matchedFilters = true;
                
                list.push(node);

                if(node[fields.parentKey]){
                    includeAncestorsInResults(node);
                };
            }
            else{
                if(alreadyAdded){
                    delete alreadyAdded.matchedFilters;
                } 
                delete node.matchedFilters;
            }
        });

        const expandedKeys = this.setExpandedKeys(this.state.expandedKeys, list.map(node => node[fields.key]));
        
        this.setState({
            nodes: this.sortNodes(list),
            expandedKeys
        });
    }

    render() {
        
        const filteredNodes = this.state.nodes;

        const nodesHierarchy = this.convertToHierarchy(filteredNodes);
        
        let className = 'treeview-container' 
        if(this.props.readonly) className += ' readonly';
        if(this.props.compact) className += ' compact';

        return <div className={className}>
                    <div className="filters">
                        <input type="text" placeholder={strings.placeholder_filter} value={this.state.keyword} onChange={this.onKeywordChange} />
                        {this.props.customFilter && 
                            React.cloneElement(this.props.customFilter, 
                            {
                                onChange: (e) => {
                                    this.onCustomFilterChange(e);
                                    this.props.customFilter.props.onChange(e);
                                }
                            })
                        }
                    </div>
                    <div>
                        {filteredNodes.length > 0 && this.renderTree(nodesHierarchy)}
                        {(this.state.keyword || this.state.customFilterValue) && filteredNodes.length === 0 && <div className='no-matches-found'>{strings.db_filter_no_matches_found}</div>}
                    </div>
                    <ConfirmDialog
                        isOpen={this.state.isConfirmOpen}
                        onOk={this.deselectNode}
                        onCancel={this.closeConfirmDialog}
                        onDismiss={this.closeConfirmDialog}
                    >
                        {this.state.confirmMessage}
                    </ConfirmDialog> 
                </div>;
    }
}

export default TreeView;
